import * as React from "react";
import { Label, StyledInput } from "../../../shared/ForwardInput";
import { Icon, AutoComplete, Row, Col, Modal, Input } from "antd";
import styled from "styled-components";
import { SelectValue, OptionProps } from "antd/lib/select";
import { AddressCreationForm } from "./AddressCreationForm";
import { Address } from "../../../lib/model/Address";
import { RootState } from "../../../store/rootReducer";
import { connect } from "react-redux";
import { AddressState } from "../../../lib/model/AddressState";
import { AddressValidator } from "../../../lib/validators/AddressValidator";

const validator = new AddressValidator();

const AddAddressSpan = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 2.29;
  color: #ffffff;
`;

const Subline = styled.span`
  font-size: 11px;
  font-size: 11px;
  font-weight: 300;
  line-height: 0.91;
  color: #061a32;
`;

const filterOption = (
  input: string,
  option: React.ReactElement<OptionProps>
): boolean => {
  if (
    ["new_1", "new_2", "loading", "empty"].indexOf(
      option?.key?.toString() || ""
    ) !== -1
  )
    return true;
  return stringContains(option.props.title || "", input);
};

const Option = AutoComplete.Option;

type OwnProps = {
  title: string;
  value: AddressState;
  onChange: (value: AddressState) => void;
};

type State = {
  dataSource: React.ReactElement<OptionProps>[];
  showCreateModal: boolean;
  searchValue?: string;
};

const mapStateToProps = (state: RootState) => ({
  addresses: state.addresses.addresses,
  isLoadingAddresses: state.addresses.isLoadingAddresses,
});

const dispatchProps = {};

type Props = ReturnType<typeof mapStateToProps> &
  typeof dispatchProps &
  OwnProps;

class _AddressInput extends React.Component<Props, State> {
  readonly state: State = {
    dataSource: [],
    showCreateModal: false,
  };
  readonly autocompleteRef = React.createRef<AutoComplete>();
  readonly inputRef = React.createRef<Input>();
  render() {
    const { value, onChange } = this.props;
    return (
      <Row type="flex">
        <Col span={24}>
          <Label>{this.props.title}</Label>
        </Col>
        <Col span={24}>
          <AutoComplete
            dataSource={this.state.dataSource}
            filterOption={filterOption}
            onSelect={this.handleSelect}
            style={{ width: "100%" }}
            dropdownStyle={{ backgroundColor: "#f2f2f2" }}
            optionLabelProp="title"
            value={
              value.id ||
              (this.props.value.create &&
                this.props.value.create.companyName) ||
              this.state.searchValue
            }
            onSearch={this.handleSearch}
            ref={this.autocompleteRef}
          >
            <StyledInput
              suffix={<Icon type="search" style={{ color: "#386ee8" }} />}
              hasError={!validator.isValidState(value)}
              ref={this.inputRef}
              onFocus={this.handleFocus}
            />
          </AutoComplete>
        </Col>
        <Modal
          visible={this.state.showCreateModal}
          onCancel={this.handleCreateClose}
          footer={null}
        >
          <AddressCreationForm
            address={value.create || { isZipValidationLoading: false }}
            onChange={(value) => onChange({ create: value })}
            onSaveClick={this.handleCreateClose}
          />
        </Modal>
      </Row>
    );
  }
  componentDidMount = () => {
    const { value } = this.props;
    this.setState({
      searchValue: value.id || (value.create && value.create.companyName),
    });
    this.refreshDataSource();
  };
  componentDidUpdate = (prevProps: Props, prevState: {}, snapshot: any) => {
    if (this.props.isLoadingAddresses !== prevProps.isLoadingAddresses) {
      this.refreshDataSource();
    }
  };
  refreshDataSource = async () => {
    const { isLoadingAddresses, addresses } = this.props;
    const cellsToShow: React.ReactElement<OptionProps>[] = [];
    if (isLoadingAddresses) {
      cellsToShow.push(
        <Option
          key="loading"
          value="loading"
          title="Adressen werden geladen..."
        >
          <Subline>
            <Icon type="loading" spin />
            Adressen werden geladen
          </Subline>
        </Option>
      );
    } else if (addresses.length === 0) {
      cellsToShow.push(
        <Option key="empty" value="empty" title="Keine Einträge">
          <Subline>Es wurde kein Eintrag im Adressbuch gefunden.</Subline>
        </Option>
      );
    } else {
      cellsToShow.push(
        ...addresses.sort(sortAddresses).map((a) => (
          <Option
            key={a.id}
            value={a.id}
            title={`${a.companyName}, \n${a.street} ${a.houseNumber}, \n${a.zipCode} ${a.city}`}
            style={{ borderBottom: "1px solid #bfbfbf" }}
          >
            <AddressCell
              title={a.companyName}
              addressNo={`${a.street} ${a.houseNumber}`}
              cityZip={`${a.zipCode} ${a.city}`}
            />
          </Option>
        ))
      );
    }

    // add two mock cells, one is static at the bottom and one for spacing behind that cell
    cellsToShow.push(
      <Option
        key="new_1"
        value="new_1"
        title="Neue Adresse"
        style={{
          backgroundColor: "#386ee8",
        }}
      >
        <AddAddressSpan>+ Neue Adresse eingeben</AddAddressSpan>
      </Option>,
      <Option
        key="new_2"
        value="new_2"
        title="Neue Adresse"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#386ee8",
        }}
      >
        <AddAddressSpan>+ Neue Adresse eingeben</AddAddressSpan>
      </Option>
    );
    this.setState({
      dataSource: cellsToShow,
    });
  };
  handleSelect = (value: SelectValue, option: any) => {
    if (value === "empty" || value === "loading") return;
    if (value === "new_1" || value === "new_2") {
      this.setState({ showCreateModal: true });
    } else if (typeof value === "string") {
      this.props.onChange({ id: value });
    }
  };
  handleSearch = (value: string) => {
    this.setState({ searchValue: value });
    this.props.onChange({});
  };
  handleFocus = () => {
    if (this.props.addresses.length === 0 && !this.props.isLoadingAddresses) {
      this.setState({ showCreateModal: true });
      this.autocompleteRef.current && this.autocompleteRef.current.blur();
      this.inputRef.current && this.inputRef.current.blur();
    }
  };
  handleCreateClose = () => {
    this.setState({ showCreateModal: false });
  };
}

export const AddressInput = connect(
  mapStateToProps,
  dispatchProps
)(_AddressInput);

const stringContains = (string: string, search: string): boolean => {
  return string.toUpperCase().indexOf(search.toUpperCase()) !== -1;
};

const sortAddresses = (lhs: Address, rhs: Address): number => {
  if (lhs.companyName.toUpperCase() < rhs.companyName.toUpperCase()) {
    return -1;
  }
  if (lhs.companyName.toUpperCase() > rhs.companyName.toUpperCase()) {
    return 1;
  }
  return 0;
};

type AddressCellProps = {
  title: string;
  addressNo: string;
  cityZip: string;
};

export const AddressCell: React.FC<AddressCellProps> = (props) => {
  const { title, addressNo, cityZip } = props;
  return (
    <Row>
      <Col>{title}</Col>
      <Col>
        <Subline>{addressNo}</Subline>
      </Col>
      <Col>
        <Subline>{cityZip}</Subline>
      </Col>
    </Row>
  );
};
